import React from 'react'
import { Remark } from 'react-remark'

import { formatCurrency, formatDate, formatDateToDDMMYYYY, makeCreditDescription } from '../utils/utils'
import { formatDocumentTitle } from '../utils/documentTitleUtils'
import DocToHeader from '../interfaces/DocInactive'
import { DocStateSpecial } from '../enums/DocStates'
import { Payment, PaymentTypeEnum } from '../interfaces/Reservation'
import { useFeatureFlag } from '../hooks/useFeatureFlag'

import DocNotes from './DocNotes'
import { DocHeader, DocHeaderType } from './DocHeader'
import { OrganizationFrame, OrganizationFrameType } from './OrganizationFrame'
import { ClientFrame, ClientFrameType } from './ClientFrame'
import { VehicleFrame, VehicleFrameType } from './VehicleFrame'
import { SalenoteDet } from './SalenotePreview'
import { DisplayValueEx, DisplayValueWithError } from './DisplayValue'
import { DocStatusHeader } from './DocStatusHeader'
import { GridCost, GridPayment, GridVeh } from './ReservationNvvDetail'
import SalenoteDetailV2, {
  TotalDisplay as TotalDisplayV2,
  VehicleDiscountSummary as VehicleDiscountSummaryV2,
} from './SalenoteDetailV2'

const SalenoteDetail: React.FC<{
  salenote: SalenoteDet
  errors: { [key: string]: string }
  esAprobada?: boolean
  esAnulada?: boolean
  showPurchaseLinks?: boolean // NEW: Control purchase link visibility
}> = ({ salenote, errors, esAprobada, esAnulada, showPurchaseLinks = false }) => {
  const { useNewDocumentUI } = useFeatureFlag()

  if (useNewDocumentUI)
    return (
      <SalenoteDetailV2
        salenote={salenote}
        errors={errors}
        esAprobada={esAprobada}
        esAnulada={esAnulada}
        showPurchaseLinks={showPurchaseLinks}
      />
    )

  const myNumberNvv = salenote?.number === 0 ? 'XX' : salenote?.number
  //  const isAnulada = !!salenote?.voidedAt

  let tituloAnulada = ''
  let fechaFinText = ''
  let fechaFinValue = ''

  if (esAnulada) {
    tituloAnulada = 'NULA'
  } else if (!esAprobada) {
    tituloAnulada = 'PENDIENTE'
  }

  if (esAprobada) {
    fechaFinText = `Fecha de aprobación`
    fechaFinValue = formatDateToDDMMYYYY(new Date(salenote?.approvedAt ?? new Date()))
  }

  // Format title using template configuration
  const defaultTitle = 'Nota de Venta'
  const formattedTitle = formatDocumentTitle(salenote.titleConfig, defaultTitle, myNumberNvv)

  const docHeader: DocHeaderType = {
    urlImage: salenote.body.organization.pictures.logotype,
    titulo: formattedTitle,
    isAnulada: esAnulada || !esAprobada,
    tituloAnulada: tituloAnulada,
    fechaIniText: `Fecha`,
    fechaIniValue: formatDate(salenote?.date),
    fechaFinText: fechaFinText,
    fechaFinValue: fechaFinValue,
  }
  const user = { name: salenote.profile_name ?? '', phone: salenote.user_phone ?? '' }

  const organizationFrame: OrganizationFrameType = {
    ...salenote.body.organization,
    user: user,
  }
  const clientFrame: ClientFrameType = {
    ...salenote.body.client,
    compraPara: salenote.compraPara,
  }

  // Use original price for vehicle display if discount exists
  const vehiclePrice = salenote.discount?.originalPrice ?? salenote.price

  const vehicleFrame: VehicleFrameType = {
    ...salenote.body.vehicle,
    price: vehiclePrice,
  }

  const costs = salenote.additionals ?? []
  const vehicles = salenote.vehicles ?? []
  const footer: string = salenote.footer ?? ''
  const price = salenote.price
  const creditAmount = salenote.credit?.amount ?? 0

  // Calculate total including additionals using original price if discount exists
  const basePrice = salenote.discount?.originalPrice ?? price
  const totalWithAdditionals = costs.reduce(
    (accumulator: number, item: { cost: number }) => Number(accumulator) + item.cost,
    basePrice
  )

  // Calculate final amount to pay (with discount if applicable)
  const finalPrice = salenote.discount?.discountedPrice ?? price
  const porReservar = costs.reduce(
    (accumulator: number, item: { cost: number }) => Number(accumulator) + item.cost,
    finalPrice
  )

  const reservado =
    (salenote.payments ?? []).reduce(
      (accumulator: number, item: { value: number }) => Number(accumulator) + item.value,
      0
    ) +
    salenote.vehicles.reduce((accumulator: number, item: { price: number }) => Number(accumulator) + item.price, 0) +
    creditAmount
  const saldo = reservado - porReservar

  // Para poder usa GridPayment

  const creditPayment: Payment[] = salenote.credit
    ? [
        {
          _id: salenote.credit?._id ?? '',
          document: makeCreditDescription(salenote.credit),
          date: formatDate(formatDateToDDMMYYYY(new Date(salenote.credit?.updatedAt ?? new Date()))),
          value: creditAmount,
        },
      ]
    : []

  const normalPayments = (salenote.payments ?? []).filter((p) => p.type !== PaymentTypeEnum.Credit)

  let docInactive: DocToHeader = {
    state: DocStateSpecial.Activo,
    date: '',
    cause: '',
    by: '',
  }

  if (salenote?.voidedAt) {
    docInactive = {
      state: DocStateSpecial.Nulo,
      date: salenote?.voidedAt ?? '',
      cause: salenote.voidedCause ?? '',
      by: salenote?.voidedBy_user ?? '',
    }
  }

  return (
    <>
      <DocStatusHeader docName="nota de venta" state={docInactive} />
      <DocHeader header={docHeader} />
      <hr className="w-full border-t border-black-500 my-2" />

      <OrganizationFrame organization={organizationFrame} />

      <hr className="w-full border-t border-black-500 my-2" />
      <ClientFrame client={clientFrame} />

      <hr className="w-full border-t border-black-500 my-2" />
      <VehicleFrame vehicle={vehicleFrame} />
      {costs?.length > 0 && (
        <div className="border-2 border-black pl-2 mt-2">
          <GridCost title="Adicionales" paycosts={costs} />
        </div>
      )}
      {salenote.discount ? (
        <TotalDisplayWithDiscount
          title="Total a pagar"
          discount={salenote.discount}
          totalWithAdditionals={totalWithAdditionals}
        />
      ) : (
        <TotalDisplay title="Total a pagar" total={porReservar} />
      )}
      <div className="border-2 border-black pl-2 mt-2">
        <GridPayment title="Pagos" payments={normalPayments ?? []} />
      </div>
      {creditPayment?.length > 0 && (
        <div className="border-2 border-black pl-2 mt-2">
          <GridPayment title="Créditos" payments={creditPayment} />
        </div>
      )}
      {vehicles?.length > 0 && (
        <div className="border-2 border-black pl-2 mt-2">
          <GridVeh
            title="Vehículos en Parte de Pago"
            paycosts={vehicles}
            showPurchaseLinks={showPurchaseLinks} // Only show links when explicitly requested
            isApproved={esAprobada}
          />
        </div>
      )}
      <TotalDisplay title="Total pagos" total={reservado} errors={errors} />
      {saldo > 0 && <TotalDisplay title="Saldo a favor del cliente" total={saldo} name="saldo" errors={errors} />}
      {!!salenote.insuranceCo && (
        <div className="border-2 border-black pl-2 pb-1 mt-2 grid grid-cols-2">
          <DisplayValueEx
            label="Compañia de seguros"
            value={salenote.insuranceCo}
            className="text-[1.5em]"
            valueClass="text-[1.25em] font-bold"
          />
        </div>
      )}
      <DocNotes notes={salenote?.notes} />
      <hr className="w-full border-t border-black-500 my-2" />
      <Remark>{footer}</Remark>
    </>
  )
}

export const TotalDisplay: React.FC<{
  title: string
  total: number
  name?: string
  errors?: { [key: string]: string }
}> = ({ title, total, name, errors }) => {
  const { useNewDocumentUI } = useFeatureFlag()

  if (useNewDocumentUI) return <TotalDisplayV2 title={title} total={total} name={name} errors={errors} />
  const total_edit = formatCurrency(total)

  return (
    <div className="pr-4 pb-1 mb-2 flex justify-between border-2 border-black pl-2 mt-2">
      <div className="text-left text-[1.5em]">
        {/* Content for the left-aligned div */}
        {title}
      </div>
      <div className="text-right text-[1.25em] ">
        {/* Content for the right-aligned div */}
        <DisplayValueWithError value={total_edit} errors={errors} name={name} />
      </div>
    </div>
  )
}

export const TotalDisplayWithDiscount: React.FC<{
  title: string
  discount: {
    originalPrice: number
    discountedPrice: number
    discountAmount: number
  }
  totalWithAdditionals: number
}> = ({ discount, totalWithAdditionals }) => {
  const { useNewDocumentUI } = useFeatureFlag()

  if (useNewDocumentUI) return <VehicleDiscountSummaryV2 discount={discount} />
  const totalAfterDiscount = totalWithAdditionals - discount.discountAmount

  return (
    <div className="border-2 border-black pl-2 mt-2 mb-2">
      <div className="pr-4 pb-1 flex justify-between">
        <div className="text-left text-[1.5em]">A Pagar</div>
        <div className="text-right text-[1.25em]">{formatCurrency(totalWithAdditionals)}</div>
      </div>
      <div className="pr-4 pb-1 flex justify-between">
        <div className="text-left text-[1.2em] pl-4">Descuento {formatCurrency(discount.discountAmount)}</div>
        <div className="text-right text-[1.25em]"></div>
      </div>
      <div className="pr-4 pb-1 flex justify-between">
        <div className="text-left text-[1.5em]">Total a pagar</div>
        <div className="text-right text-[1.25em]">{formatCurrency(totalAfterDiscount)}</div>
      </div>
    </div>
  )
}

export default SalenoteDetail
